import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import salesReturnService, {
  type SalesReturnListParams,
  type SalesReturnPayload,
  type SalesReturnRecord,
} from "@/services/salesReturn";
import salesService, { type Sale, type SaleItem } from "@/services/sales";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import customerService, { type Customer } from "@/services/customer";
import { useCurrentUser } from "@/composables/useCurrentUser";

type SalesReturnFormItem = {
  product_id: number;
  product_code: string;
  product_name: string;
  product_type: number;
  unit_price: number;
  quantity: number;
  max_quantity: number;
  subtotal: number;
  total: number;
  discount: number;
  tax: number;
  cost: number;
};

type SalesReturnFormState = {
  date: string;
  return_ref: string;
  sale_id: number | null;
  note: string;
  status: string;
  discount: number;
  tax: number;
  shipping: number;
  customer_name: string;
  warehouse_name: string;
  reference_no: string;
};

const formatDateInput = (value?: string) => {
  if (!value) return new Date().toISOString().slice(0, 16);

  const date = new Date(value);
  const localOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - localOffset).toISOString().slice(0, 16);
};

const toNumber = (value?: number | string | null) => Number(value ?? 0);

const buildInitialForm = (): SalesReturnFormState => ({
  date: formatDateInput(),
  return_ref: "",
  sale_id: null,
  note: "",
  status: "completed",
  discount: 0,
  tax: 0,
  shipping: 0,
  customer_name: "",
  warehouse_name: "",
  reference_no: "",
});

const mapSaleItemToReturnItem = (
  item: SaleItem,
  quantity = 1,
  fallback?: Partial<SalesReturnFormItem>
): SalesReturnFormItem => {
  const maxQuantity = Math.max(1, toNumber(item.quantity));
  const safeQuantity = Math.min(Math.max(1, quantity), maxQuantity);
  const unitPrice = toNumber(item.unit_price ?? item.price);

  return {
    product_id: Number(item.product_id),
    product_code: item.product_code || "",
    product_name: item.product_name || "",
    product_type: Number((item as SaleItem & { product_type?: number }).product_type ?? 1),
    unit_price: unitPrice,
    quantity: safeQuantity,
    max_quantity: maxQuantity,
    subtotal: unitPrice * safeQuantity,
    total: unitPrice * safeQuantity,
    discount: toNumber(fallback?.discount),
    tax: toNumber(fallback?.tax),
    cost: toNumber(fallback?.cost ?? unitPrice),
  };
};

export function useSalesReturnList() {
  const { t } = useI18n();
  const { fetchCurrentUser } = useCurrentUser();

  const loading = ref(false);
  const salesReturns = ref<SalesReturnRecord[]>([]);
  const selectedSalesReturn = ref<SalesReturnRecord | null>(null);
  const warehouses = ref<Warehouse[]>([]);
  const customers = ref<Customer[]>([]);
  const searchQuery = ref("");
  const profileWarehouseId = ref<number | null>(null);

  const filters = reactive({
    warehouse_id: 0,
    customer_id: 0,
    status: "",
  });

  let searchTimer: number | undefined;

  const fetchFilterOptions = async () => {
    const [warehousesRes, customersRes, currentUser] = await Promise.allSettled([
      warehouseService.getWarehouses(),
      customerService.getCustomers(),
      fetchCurrentUser(true),
    ]);

    if (warehousesRes.status === "fulfilled") {
      warehouses.value = warehousesRes.value;
    }

    if (customersRes.status === "fulfilled") {
      customers.value = customersRes.value;
    }

    if (currentUser.status === "fulfilled" && currentUser.value?.warehouse_id) {
      profileWarehouseId.value = Number(currentUser.value.warehouse_id);
      filters.warehouse_id = profileWarehouseId.value;
    }
  };

  const buildParams = (): SalesReturnListParams => ({
    q: searchQuery.value || undefined,
    warehouse_id: filters.warehouse_id || undefined,
    customer_id: filters.customer_id || undefined,
    status: filters.status || undefined,
    per_page: 10,
    page: 1,
  });

  const fetchSalesReturns = async () => {
    loading.value = true;
    try {
      const response = await salesReturnService.getSalesReturns(buildParams());
      salesReturns.value = response.data ?? [];
    } catch (error) {
      console.error("Failed to fetch sales returns:", error);
      Swal.fire(t("sales_return_page.failed_to_load"), t("sales_return_page.unable_to_load_sales_returns"), "error");
    } finally {
      loading.value = false;
    }
  };

  const handleViewDetails = async (record: SalesReturnRecord) => {
    try {
      selectedSalesReturn.value = await salesReturnService.getSalesReturn(record.id);
    } catch (error) {
      console.error("Failed to fetch sales return details:", error);
      Swal.fire(t("sales_return_page.failed_to_load"), t("sales_return_page.unable_to_load_sales_return"), "error");
    }
  };

  const handleDeleteSalesReturn = async (record: SalesReturnRecord) => {
    const result = await Swal.fire({
      title: t("sales_return_page.delete_sales_return_title"),
      text: t("sales_return_page.delete_sales_return_text", { ref: record.return_ref }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("sales_return_page.confirm_delete"),
      confirmButtonColor: "#d33c43",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await salesReturnService.deleteSalesReturn(record.id);
      await Swal.fire(t("sales_return_page.deleted"), t("sales_return_page.sales_return_deleted_successfully"), "success");
      await fetchSalesReturns();
      selectedSalesReturn.value = null;
    } catch (error: any) {
      console.error("Failed to delete sales return:", error);
      Swal.fire(t("sales_return_page.delete_failed"), error?.response?.data?.message || t("sales_return_page.unable_to_delete_sales_return"), "error");
    }
  };

  const resetFilters = async () => {
    filters.customer_id = 0;
    filters.status = "";
    filters.warehouse_id = profileWarehouseId.value ?? 0;
    searchQuery.value = "";
    await fetchSalesReturns();
  };

  const applyFilters = async () => {
    await fetchSalesReturns();
  };

  watch(searchQuery, (value) => {
    if (searchTimer) {
      window.clearTimeout(searchTimer);
    }

    searchTimer = window.setTimeout(() => {
      if (value === searchQuery.value) {
        fetchSalesReturns();
      }
    }, 300);
  });

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchSalesReturns();
  });

  return {
    loading,
    salesReturns,
    selectedSalesReturn,
    warehouses,
    customers,
    searchQuery,
    filters,
    profileWarehouseId,
    fetchSalesReturns,
    handleViewDetails,
    handleDeleteSalesReturn,
    resetFilters,
    applyFilters,
  };
}

function buildSalesReturnForm(mode: "create" | "edit", salesReturnId?: number) {
  const { t } = useI18n();
  const router = useRouter();
  const route = useRoute();
  const { fetchCurrentUser } = useCurrentUser();

  const isLoading = ref(false);
  const isSubmitting = ref(false);
  const saleSearch = ref("");
  const saleSearchResults = ref<Sale[]>([]);
  const showSaleResults = ref(false);
  const selectedSale = ref<Sale | null>(null);
  const items = ref<SalesReturnFormItem[]>([]);
  const profileWarehouseId = ref<number | null>(null);

  const breadcrumbItems =
    mode === "edit"
      ? [
          { label: "sales_return_list", to: "/sales-return-list" },
          { label: "edit_sales_return" },
        ]
      : [
          { label: "sales_return_list", to: "/sales-return-list" },
          { label: "create_sales_return" },
        ];

  const form = reactive<SalesReturnFormState>(buildInitialForm());

  const subTotal = computed(() =>
    items.value.reduce((sum, item) => sum + toNumber(item.subtotal), 0)
  );
  const grandTotal = computed(() =>
    Math.max(0, subTotal.value + toNumber(form.tax) + toNumber(form.shipping) - toNumber(form.discount))
  );
  const totalItems = computed(() =>
    items.value.reduce((sum, item) => sum + toNumber(item.quantity), 0)
  );

  const updateDerivedFields = () => {
    form.customer_name = selectedSale.value?.customer_name || selectedSale.value?.customer?.name || "";
    form.warehouse_name = selectedSale.value?.warehouse_name || selectedSale.value?.warehouse?.name || "";
    form.reference_no = selectedSale.value?.reference_no || "";
    form.sale_id = selectedSale.value?.id || null;
  };

  const setItemsFromSale = (sale: Sale, existingReturn?: SalesReturnRecord | null) => {
    const existingByProductId = new Map<number, number>();

    if (existingReturn?.items?.length) {
      existingReturn.items.forEach((item) => {
        existingByProductId.set(Number(item.product_id), toNumber(item.quantity));
      });
    }

    items.value = (sale.items || []).map((item) =>
      mapSaleItemToReturnItem(item, existingByProductId.get(Number(item.product_id)) ?? 1)
    );
  };

  const searchSales = async (query = "") => {
    try {
      const response = await salesService.getSales({
        q: query || undefined,
        warehouse_id: profileWarehouseId.value || undefined,
        per_page: 10,
        page: 1,
      });

      saleSearchResults.value = response.data ?? [];
      showSaleResults.value = true;
    } catch (error) {
      console.error("Failed to search sales:", error);
      saleSearchResults.value = [];
      showSaleResults.value = true;
    }
  };

  const handleSaleSearch = async (value: string) => {
    saleSearch.value = value;
    await searchSales(value.trim() === "" ? "" : value);
  };

  const selectSale = async (sale: Sale) => {
    try {
      const saleDetails = await salesService.getSaleDetails(sale.id);
      selectedSale.value = saleDetails;
      saleSearch.value = saleDetails.reference_no || "";
      updateDerivedFields();
      setItemsFromSale(saleDetails);
      showSaleResults.value = false;
    } catch (error) {
      console.error("Failed to load sale details:", error);
      Swal.fire(t("sales_return_page.failed_to_load"), t("sales_return_page.unable_to_load_selected_sale"), "error");
    }
  };

  const updateItemQuantity = (productId: number, nextQuantity: number) => {
    items.value = items.value.map((item) => {
      if (item.product_id !== productId) {
        return item;
      }

      const safeQuantity = Math.min(Math.max(1, nextQuantity), item.max_quantity);
      return {
        ...item,
        quantity: safeQuantity,
        subtotal: toNumber(item.unit_price) * safeQuantity,
        total: toNumber(item.unit_price) * safeQuantity,
      };
    });
  };

  const removeItem = (productId: number) => {
    items.value = items.value.filter((item) => item.product_id !== productId);
  };

  const fetchCreateDefaults = async () => {
    form.return_ref = await salesReturnService.getNextReference();
  };

  const fetchEditData = async () => {
    if (!salesReturnId) {
      return;
    }

    const salesReturn = await salesReturnService.getSalesReturn(salesReturnId);
    const saleDetails = await salesService.getSaleDetails(Number(salesReturn.sale_id));

    selectedSale.value = saleDetails;
    updateDerivedFields();

    form.date = formatDateInput(salesReturn.created_at);
    form.return_ref = salesReturn.return_ref;
    form.note = salesReturn.note || "";
    form.status = salesReturn.status || "completed";
    form.discount = toNumber(salesReturn.discount ?? salesReturn.total_discount);
    form.tax = toNumber(salesReturn.tax);
    form.shipping = toNumber(salesReturn.shipping);
    form.sale_id = Number(salesReturn.sale_id);
    form.reference_no = salesReturn.reference_no || saleDetails.reference_no;
    form.customer_name = salesReturn.customer_name || saleDetails.customer_name || "";
    form.warehouse_name = salesReturn.warehouse_name || saleDetails.warehouse_name || "";
    saleSearch.value = saleDetails.reference_no || "";

    setItemsFromSale(saleDetails, salesReturn);
  };

  const initialize = async () => {
    isLoading.value = true;
    try {
      const currentUser = await fetchCurrentUser(true);
      profileWarehouseId.value = currentUser?.warehouse_id ? Number(currentUser.warehouse_id) : null;

      if (mode === "edit") {
        await fetchEditData();
      } else {
        await fetchCreateDefaults();
      }
    } catch (error) {
      console.error(`Failed to initialize ${mode} sales return form:`, error);
      Swal.fire(
        t("sales_return_page.failed_to_load"),
        mode === "edit"
          ? t("sales_return_page.unable_to_load_edit_sales_return_data")
          : t("sales_return_page.unable_to_load_create_sales_return_data"),
        "error"
      );
      if (mode === "edit") {
        router.push("/sales-return-list");
      }
    } finally {
      isLoading.value = false;
    }
  };

  const buildPayload = (): SalesReturnPayload => {
    if (!selectedSale.value || !form.sale_id) {
      throw new Error("Please select an original sale first.");
    }

    return {
      sale_id: form.sale_id,
      warehouse_id: Number(selectedSale.value.warehouse_id),
      return_ref: form.return_ref,
      reference_no: form.reference_no,
      customer_id: selectedSale.value.customer_id || null,
      customer_code: selectedSale.value.customer?.code || null,
      customer_name: form.customer_name || null,
      warehouse_code: selectedSale.value.warehouse?.code || null,
      warehouse_name: form.warehouse_name || null,
      note: form.note || undefined,
      status: form.status,
      discount: toNumber(form.discount),
      total_discount: toNumber(form.discount),
      tax: toNumber(form.tax),
      shipping: toNumber(form.shipping),
      total: subTotal.value,
      grand_total: grandTotal.value,
      paid: 0,
      items: items.value.map((item) => ({
        product_id: item.product_id,
        product_code: item.product_code,
        product_name: item.product_name,
        product_type: item.product_type,
        quantity: item.quantity,
        unit_price: item.unit_price,
        price: item.unit_price,
        subtotal: item.subtotal,
        total: item.total,
        tax: item.tax,
        discount: item.discount,
        cost: item.cost,
      })),
    };
  };

  const submitSalesReturn = async () => {
    if (!selectedSale.value || !form.sale_id) {
      await Swal.fire(t("sales_return_page.original_sale_required"), t("sales_return_page.original_sale_required_text"), "warning");
      return;
    }

    if (!items.value.length) {
      await Swal.fire(t("sales_return_page.no_items"), t("sales_return_page.no_items_text"), "warning");
      return;
    }

    isSubmitting.value = true;
    try {
      const payload = buildPayload();

      if (mode === "edit" && salesReturnId) {
        await salesReturnService.updateSalesReturn(salesReturnId, payload);
      } else {
        await salesReturnService.createSalesReturn(payload);
      }

      await Swal.fire(
        t("sales_return_page.success"),
        mode === "edit"
          ? t("sales_return_page.sales_return_updated_successfully")
          : t("sales_return_page.sales_return_created_successfully"),
        "success"
      );
      router.push("/sales-return-list");
    } catch (error: any) {
      console.error(`Failed to ${mode} sales return:`, error);
      Swal.fire(
        mode === "edit" ? t("sales_return_page.update_failed") : t("sales_return_page.create_failed"),
        error?.response?.data?.message || t("sales_return_page.unable_to_save_sales_return"),
        "error"
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  watch(
    () => route.params.id,
    (value) => {
      if (mode === "edit" && value) {
        const numericId = Number(value);
        if (!Number.isNaN(numericId) && numericId !== salesReturnId) {
          window.location.reload();
        }
      }
    }
  );

  onMounted(initialize);

  return {
    breadcrumbItems,
    form,
    isLoading,
    isSubmitting,
    saleSearch,
    saleSearchResults,
    showSaleResults,
    selectedSale,
    items,
    subTotal,
    grandTotal,
    totalItems,
    handleSaleSearch,
    searchSales,
    selectSale,
    updateItemQuantity,
    removeItem,
    submitSalesReturn,
  };
}

export function useSalesReturnForm() {
  return buildSalesReturnForm("create");
}

export function useSalesReturnEditForm(salesReturnId?: number) {
  const route = useRoute();
  const resolvedId = salesReturnId ?? Number(route.params.id);
  return buildSalesReturnForm("edit", resolvedId);
}
